/**
 * AiThumbnailPicker — captures 3 frames from the LiveBroadcaster's local
 * camera <video> element, sends them to /api/live/{id}/auto-thumbnail,
 * and previews the AI-picked winner. Updates the stream's `thumbnail_url`
 * in the backend.
 *
 * Frames are captured at 0s / 1s / 2s of "now" — the broadcaster is
 * already on-air so we don't need long spacing; what we want is variety
 * of microexpressions.  The component is render-flat: tap "AI thumbnail"
 * → 3 frames captured → POST JSON → toast + preview chip.
 *
 * Falls back gracefully:
 *   • If no <video> with srcObject is available, captures the current
 *     poster image instead (one frame × 3 — Gemini will still pick "best".
 *   • If the LLM call fails, the backend returns winner_index=0 anyway
 *     and we still update the thumbnail.
 */
import React, { useState, useRef } from "react";
import { Sparkles, Camera, RefreshCw, Check } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";

export default function AiThumbnailPicker({ streamId, getVideoEl, currentThumbnail, onChosen, autoIntervalSec = 0 }) {
  const [busy, setBusy] = useState(false);
  const [winner, setWinner] = useState(null); // { thumbnail_url, top_reason, scores, ai_used }
  const [auto, setAuto] = useState(false);
  const canvasRef = useRef(null);
  const autoTimerRef = useRef(null);

  const captureFrame = async () => {
    const v = (typeof getVideoEl === "function" ? getVideoEl() : null) ||
              document.querySelector('[data-testid="live-broadcaster-preview"] video') ||
              document.querySelector('video');
    const c = canvasRef.current;
    if (!c) return null;
    if (v && v.videoWidth) {
      c.width = Math.min(v.videoWidth, 1280);
      c.height = Math.round((c.width / v.videoWidth) * v.videoHeight);
      const ctx = c.getContext("2d");
      ctx.drawImage(v, 0, 0, c.width, c.height);
    } else if (currentThumbnail) {
      // Fallback: load the existing thumbnail into the canvas (lets the
      // backend at least re-score the static art).
      const img = new Image();
      img.crossOrigin = "anonymous";
      await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = currentThumbnail; });
      c.width = img.width || 1280; c.height = img.height || 720;
      c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
    } else {
      return null;
    }
    return c.toDataURL("image/jpeg", 0.85).split(",")[1];
  };

  const run = async () => {
    if (!streamId) return;
    setBusy(true);
    try {
      const frames = [];
      for (let i = 0; i < 3; i++) {
        const f = await captureFrame();
        if (f) frames.push(f);
        if (i < 2) await new Promise((r) => setTimeout(r, 2500));
      }
      if (frames.length < 3) {
        toast.error("Couldn't capture 3 frames — start camera first");
        return;
      }
      const { data } = await api.post(`/live/${streamId}/auto-thumbnail`, { frames });
      setWinner(data);
      onChosen?.(data.thumbnail_url);
      if (data.ai_used) {
        toast.success(`✨ AI picked frame ${data.winner_index + 1}: "${data.top_reason || "best moment"}"`);
      } else {
        toast.success(`Thumbnail updated (frame 1) — AI scoring unavailable`);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  // Auto-mode: every N seconds (default 90s when toggled on) re-pick the
  // best thumbnail so the discovery rail never freezes on the host's avatar.
  React.useEffect(() => {
    if (!auto || !streamId) return;
    const interval = (autoIntervalSec || 90) * 1000;
    const tick = () => { if (!busy) run(); };
    autoTimerRef.current = setInterval(tick, interval);
    // Fire one immediately so the first thumbnail lands quickly.
    setTimeout(tick, 1500);
    return () => { if (autoTimerRef.current) clearInterval(autoTimerRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auto, streamId, autoIntervalSec]);

  return (
    <div data-testid="ai-thumb-picker" className="bg-gradient-to-r from-purple-600/15 via-pink-500/10 to-amber-500/15 border border-purple-500/25 rounded-xl px-3 py-2 flex items-center gap-3">
      <canvas ref={canvasRef} className="hidden" />
      {winner?.thumbnail_url ? (
        <div className="relative w-16 h-10 rounded-md overflow-hidden border border-emerald-400/40 shrink-0">
          <img src={winner.thumbnail_url} alt="" className="w-full h-full object-cover" />
          <span className="absolute top-0.5 left-0.5 bg-emerald-500 text-white text-[8px] font-black rounded px-1 inline-flex items-center"><Check className="w-2.5 h-2.5" /></span>
        </div>
      ) : currentThumbnail ? (
        <div className="w-16 h-10 rounded-md overflow-hidden bg-black border border-white/15 shrink-0">
          <img src={currentThumbnail} alt="" className="w-full h-full object-cover opacity-70" />
        </div>
      ) : (
        <div className="w-16 h-10 rounded-md bg-black/40 border border-white/15 flex items-center justify-center shrink-0">
          <Camera className="w-4 h-4 text-white/30" />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-[0.2em] text-purple-300 font-black inline-flex items-center gap-1">
          <Sparkles className="w-3 h-3" /> AI thumbnail
          {auto && <span className="ml-1 text-emerald-300 text-[8px]">· AUTO</span>}
        </div>
        <div className="text-white/80 text-xs truncate mt-0.5">
          {winner?.top_reason || "Capture 3 frames and let Gemini pick the click-iest one."}
        </div>
      </div>
      <label className="text-[10px] text-white/50 inline-flex items-center gap-1 cursor-pointer">
        <input
          type="checkbox"
          data-testid="ai-thumb-auto"
          checked={auto}
          onChange={(e) => setAuto(e.target.checked)}
          className="accent-purple-400"
        />
        Auto
      </label>
      <button
        data-testid="ai-thumb-pick-btn"
        onClick={run}
        disabled={busy}
        className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-400 hover:to-pink-400 text-white text-xs font-black px-3 py-1.5 rounded-full inline-flex items-center gap-1 disabled:opacity-60"
      >
        {busy ? <><RefreshCw className="w-3 h-3 animate-spin" /> Scoring…</> : <><Sparkles className="w-3 h-3" /> Pick</>}
      </button>
    </div>
  );
}
